import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Link as LinkIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { gplOrdersApi, deliveriesApi, suppliersApi } from '../../services/api';

// Card per Ordine
const OrderCard = ({ order, onLink }) => {
  const deliveryCode = order.plant?.delivery_code || order.contract?.cod_con || 'N/D';
  const plantName = order.plant?.nome || order.contract?.nome || 'N/D';
  const plantAddress = order.plant?.indirizzo || order.contract?.localita || '';
  const plantCity = order.plant?.citta || order.contract?.citta || '';
  const fullAddress = [plantAddress, plantCity].filter(Boolean).join(', ') || 'N/D';
  
  const formatDate = (date) => {
    if (!date) return 'N/D';
    return new Date(date).toLocaleDateString('it-IT');
  };

  const formatQuantity = (qty) => {
    return parseFloat(qty || 0).toLocaleString('it-IT') + ' L';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'delivered': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return 'Confermato';
      case 'pending': return 'In Attesa';
      case 'delivered': return 'Consegnato';
      default: return status;
    }
  };

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        mb: 2, 
        borderColor: 'purple.200',
        '&:hover': { 
          borderColor: 'purple.400',
          boxShadow: 2
        }
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              {order.order_code || `ORD-${order.id}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cod. Consegna: {deliveryCode} - {fullAddress}
            </Typography>
          </Box>
          <Chip 
            label={getStatusLabel(order.status)} 
            size="small" 
            color={getStatusColor(order.status)}
          />
        </Box>
        
        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
          {plantName}
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Data Ordine</Typography>
            <Typography variant="body2">{formatDate(order.order_date)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Quantità</Typography>
            <Typography variant="body2" fontWeight="bold" color="primary">
              {formatQuantity(order.quantity_liters)}
            </Typography>
          </Grid>
        </Grid>

        {order.supplier && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Fornitore</Typography>
            <Typography variant="body2">{order.supplier.company_name}</Typography>
          </Box>
        )}
      </CardContent>
      <CardActions>
        <Button 
          size="small" 
          variant="outlined"
          startIcon={<LinkIcon />}
          onClick={() => onLink(order)}
          fullWidth
          sx={{ borderColor: 'purple.300', color: 'purple.700' }}
        >
          Collega a Rifornimento
        </Button>
      </CardActions>
    </Card>
  );
};

// Card per Rifornimento
const DeliveryCard = ({ delivery, onLink }) => {
  const deliveryCode = delivery.plant_code || delivery.plant?.delivery_code || 'N/D';
  const plantName = delivery.plant?.nome || delivery.contract?.nome || 'N/D';
  // Usa prima plant_address dalla fattura XML, altrimenti fallback su plant/contract
  const fullAddress = delivery.plant_address || 
    [delivery.plant?.indirizzo, delivery.plant?.citta].filter(Boolean).join(', ') ||
    [delivery.contract?.localita, delivery.contract?.citta].filter(Boolean).join(', ') || 
    'N/D';
  
  const formatDate = (date) => {
    if (!date) return 'N/D';
    return new Date(date).toLocaleDateString('it-IT');
  };

  const formatQuantity = (qty) => {
    return parseFloat(qty || 0).toLocaleString('it-IT') + ' L';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        mb: 2, 
        borderColor: 'success.200',
        '&:hover': { 
          borderColor: 'success.400',
          boxShadow: 2
        }
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color="success.main">
              {delivery.delivery_code || `DEL-${delivery.id}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cod. Consegna: {deliveryCode} - {fullAddress}
            </Typography>
          </Box>
          {delivery.supplierInvoice && (
            <Chip 
              label="Fatturato" 
              size="small" 
              color="info"
            />
          )}
        </Box>
        
        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
          {plantName}
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Data Consegna</Typography>
            <Typography variant="body2">{formatDate(delivery.delivery_date)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Quantità</Typography>
            <Typography variant="body2" fontWeight="bold" color="success.main">
              {formatQuantity(delivery.quantity_liters)}
            </Typography>
          </Grid>
        </Grid>

        {delivery.ddt_number && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">DDT</Typography>
            <Typography variant="body2">{delivery.ddt_number}</Typography>
          </Box>
        )}

        {delivery.supplier && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Fornitore</Typography>
            <Typography variant="body2">{delivery.supplier.company_name}</Typography>
          </Box>
        )}

        {delivery.total_amount > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Importo</Typography>
            <Typography variant="body2">{formatCurrency(delivery.total_amount)}</Typography>
          </Box>
        )}
      </CardContent>
      <CardActions>
        <Button 
          size="small" 
          variant="outlined"
          startIcon={<LinkIcon />}
          onClick={() => onLink(delivery)}
          fullWidth
          color="success"
        >
          Collega ad Ordine
        </Button>
      </CardActions>
    </Card>
  );
};

// Componente principale Tab Riconciliazione
const ReconciliationTab = ({ onOpenLinkingModal }) => {
  const queryClient = useQueryClient();
  
  // Filtri
  const [filters, setFilters] = useState({
    supplier: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'date_desc'
  });

  // Query ordini non collegati
  const unlinkedOrdersQuery = useQuery({
    queryKey: ['unlinkedOrders'],
    queryFn: () => gplOrdersApi.getUnlinked()
  });

  // Query rifornimenti non collegati
  const unlinkedDeliveriesQuery = useQuery({
    queryKey: ['unlinkedDeliveries'],
    queryFn: () => deliveriesApi.getUnlinked()
  });

  // Query statistiche riconciliazione
  const statsQuery = useQuery({
    queryKey: ['reconciliationStats'],
    queryFn: () => gplOrdersApi.getReconciliationStats()
  });

  // Query fornitori per filtro
  const suppliersQuery = useQuery({
    queryKey: ['suppliersForFilter'],
    queryFn: async () => {
      const response = await suppliersApi.getAll({ limit: 100 });
      return response.data;
    }
  });

  // Ordini filtrati e ordinati
  const filteredOrders = useMemo(() => {
    const rawData = unlinkedOrdersQuery.data?.data?.data;
    let orders = Array.isArray(rawData) ? rawData : [];
    
    // Filtra per fornitore
    if (filters.supplier) {
      orders = orders.filter(o => o.supplier_id === parseInt(filters.supplier));
    }
    
    // Filtra per data
    if (filters.dateFrom) {
      orders = orders.filter(o => new Date(o.order_date) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      orders = orders.filter(o => new Date(o.order_date) <= new Date(filters.dateTo));
    }

    // Ordina
    switch (filters.sortBy) {
      case 'date_asc':
        orders.sort((a, b) => new Date(a.order_date) - new Date(b.order_date));
        break;
      case 'date_desc':
        orders.sort((a, b) => new Date(b.order_date) - new Date(a.order_date));
        break;
      case 'quantity_desc':
        orders.sort((a, b) => parseFloat(b.quantity_liters) - parseFloat(a.quantity_liters));
        break;
      case 'quantity_asc':
        orders.sort((a, b) => parseFloat(a.quantity_liters) - parseFloat(b.quantity_liters));
        break;
      default:
        break;
    }

    return orders;
  }, [unlinkedOrdersQuery.data, filters]);

  // Rifornimenti filtrati e ordinati
  const filteredDeliveries = useMemo(() => {
    const rawData = unlinkedDeliveriesQuery.data?.data?.data;
    let deliveries = Array.isArray(rawData) ? rawData : [];
    
    // Filtra per fornitore
    if (filters.supplier) {
      deliveries = deliveries.filter(d => d.supplier_id === parseInt(filters.supplier));
    }
    
    // Filtra per data
    if (filters.dateFrom) {
      deliveries = deliveries.filter(d => new Date(d.delivery_date) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      deliveries = deliveries.filter(d => new Date(d.delivery_date) <= new Date(filters.dateTo));
    }

    // Ordina
    switch (filters.sortBy) {
      case 'date_asc':
        deliveries.sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date));
        break;
      case 'date_desc':
        deliveries.sort((a, b) => new Date(b.delivery_date) - new Date(a.delivery_date));
        break;
      case 'quantity_desc':
        deliveries.sort((a, b) => parseFloat(b.quantity_liters) - parseFloat(a.quantity_liters));
        break;
      case 'quantity_asc':
        deliveries.sort((a, b) => parseFloat(a.quantity_liters) - parseFloat(b.quantity_liters));
        break;
      default:
        break;
    }

    return deliveries;
  }, [unlinkedDeliveriesQuery.data, filters]);

  const handleRefresh = () => {
    queryClient.invalidateQueries(['unlinkedOrders']);
    queryClient.invalidateQueries(['unlinkedDeliveries']);
    queryClient.invalidateQueries(['reconciliationStats']);
  };

  const handleClearFilters = () => {
    setFilters({
      supplier: '',
      dateFrom: '',
      dateTo: '',
      sortBy: 'date_desc'
    });
  };

  const handleLinkOrder = (order) => {
    onOpenLinkingModal({
      source: order,
      type: 'order-to-delivery'
    });
  };

  const handleLinkDelivery = (delivery) => {
    onOpenLinkingModal({
      source: delivery,
      type: 'delivery-to-order'
    });
  };

  const isLoading = unlinkedOrdersQuery.isLoading || unlinkedDeliveriesQuery.isLoading;
  const hasFilters = filters.supplier || filters.dateFrom || filters.dateTo;

  return (
    <Box>
      {/* Statistiche riepilogative */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Alert 
            severity="warning" 
            icon={<ShoppingCartIcon />}
            sx={{ height: '100%' }}
          >
            <Typography variant="subtitle2">Ordini da Riconciliare</Typography>
            <Typography variant="h5">
              {statsQuery.data?.data?.data?.unlinkedOrders || filteredOrders.length}
            </Typography>
          </Alert>
        </Grid>
        <Grid item xs={12} md={4}>
          <Alert 
            severity="info" 
            icon={<LocalShippingIcon />}
            sx={{ height: '100%' }}
          >
            <Typography variant="subtitle2">Rifornimenti da Riconciliare</Typography>
            <Typography variant="h5">
              {statsQuery.data?.data?.data?.unlinkedDeliveries || filteredDeliveries.length}
            </Typography>
          </Alert>
        </Grid>
        <Grid item xs={12} md={4}>
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon />}
            sx={{ height: '100%' }}
          >
            <Typography variant="subtitle2">Già Collegati</Typography>
            <Typography variant="h5">
              {statsQuery.data?.data?.data?.linkedOrders || 0}
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      {/* Filtri */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FilterListIcon color="action" />
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Fornitore</InputLabel>
            <Select
              value={filters.supplier}
              label="Fornitore"
              onChange={(e) => setFilters(prev => ({ ...prev, supplier: e.target.value }))}
            >
              <MenuItem value="">Tutti</MenuItem>
              {(suppliersQuery.data?.data || []).map(s => (
                <MenuItem key={s.id} value={s.id}>{s.company_name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            type="date"
            size="small"
            label="Da Data"
            value={filters.dateFrom}
            onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />

          <TextField
            type="date"
            size="small"
            label="A Data"
            value={filters.dateTo}
            onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Ordina per</InputLabel>
            <Select
              value={filters.sortBy}
              label="Ordina per"
              onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
            >
              <MenuItem value="date_desc">Data (recenti prima)</MenuItem>
              <MenuItem value="date_asc">Data (meno recenti)</MenuItem>
              <MenuItem value="quantity_desc">Quantità (maggiore)</MenuItem>
              <MenuItem value="quantity_asc">Quantità (minore)</MenuItem>
            </Select>
          </FormControl>

          {hasFilters && (
            <Tooltip title="Rimuovi filtri">
              <IconButton onClick={handleClearFilters} size="small">
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            Aggiorna
          </Button>
        </Box>
      </Paper>

      {/* Layout a 2 colonne */}
      <Grid container spacing={3}>
        {/* Colonna Sinistra: Ordini */}
        <Grid item xs={12} md={6}>
          <Paper 
            sx={{ 
              p: 2, 
              bgcolor: 'rgba(156, 39, 176, 0.04)',
              borderTop: '4px solid',
              borderColor: 'purple.main',
              minHeight: 600
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Badge badgeContent={filteredOrders.length} color="secondary">
                <ShoppingCartIcon color="secondary" />
              </Badge>
              <Typography variant="h6" color="secondary">
                Ordini da Riconciliare
              </Typography>
            </Box>

            {unlinkedOrdersQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredOrders.length === 0 ? (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                ✓ Tutti gli ordini sono già collegati!
              </Alert>
            ) : (
              <Box sx={{ maxHeight: 550, overflow: 'auto', pr: 1 }}>
                {filteredOrders.map(order => (
                  <OrderCard 
                    key={order.id} 
                    order={order} 
                    onLink={handleLinkOrder}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Colonna Destra: Rifornimenti */}
        <Grid item xs={12} md={6}>
          <Paper 
            sx={{ 
              p: 2, 
              bgcolor: 'rgba(76, 175, 80, 0.04)',
              borderTop: '4px solid',
              borderColor: 'success.main',
              minHeight: 600
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Badge badgeContent={filteredDeliveries.length} color="success">
                <LocalShippingIcon color="success" />
              </Badge>
              <Typography variant="h6" color="success.main">
                Rifornimenti da Riconciliare
              </Typography>
            </Box>

            {unlinkedDeliveriesQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredDeliveries.length === 0 ? (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                ✓ Tutti i rifornimenti sono già collegati!
              </Alert>
            ) : (
              <Box sx={{ maxHeight: 550, overflow: 'auto', pr: 1 }}>
                {filteredDeliveries.map(delivery => (
                  <DeliveryCard 
                    key={delivery.id} 
                    delivery={delivery} 
                    onLink={handleLinkDelivery}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReconciliationTab;
